import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { validateField } from '../util/validation.js'

export const useMiPerfilForm = (initialData = {}) => {
  // Guardamos los datos iniciales en una ref para que no cambien
  const initialDataRef = useRef(initialData)

  const [apellido, setApellido] = useState('')
  const [nombre, setNombre] = useState('')
  const [tipoTelefonoPrincipal, setTipoTelefonoPrincipal] = useState('')
  const [numeroTelefonoPrincipal, setNumeroTelefonoPrincipal] = useState('')
  const [tipoTelefonoAlternativo, setTipoTelefonoAlternativo] = useState('')
  const [numeroTelefonoAlternativo, setNumeroTelefonoAlternativo] = useState('')
  const [isPhoneValid, setIsPhoneValid] = useState(true)
  const [isAltPhoneValid, setIsAltPhoneValid] = useState(true)
  // Snapshot reactivo del initialData: espeja initialDataRef para que hasChanges
  // se recalcule cuando syncInitialData actualiza el baseline tras guardar
  // (las refs no son reactivas y no disparan re-render)
  const [initialSnapshot, setInitialSnapshot] = useState(initialData)

  // Efecto para inicializar valores SOLO la primera vez que llegan datos válidos
  useEffect(() => {
    // Solo inicializar si tenemos datos y los estados están vacíos
    const hasInitialData = initialData.apellido || initialData.nombre
    const statesAreEmpty = !apellido && !nombre

    if (hasInitialData && statesAreEmpty) {
      // Guardar los datos iniciales en la ref y en el snapshot reactivo
      initialDataRef.current = initialData
      setInitialSnapshot(initialData)

      setApellido(initialData.apellido || '')
      setNombre(initialData.nombre || '')
      setTipoTelefonoPrincipal(initialData.tipoTelefonoPrincipal || '')
      setNumeroTelefonoPrincipal(initialData.numeroTelefonoPrincipal || '')
      setTipoTelefonoAlternativo(initialData.tipoTelefonoAlternativo || '')
      setNumeroTelefonoAlternativo(initialData.numeroTelefonoAlternativo || '')
    }
  }, [initialData, apellido, nombre])

  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})

  const getError = useCallback((field) => (touched[field] ? errors[field] || null : null), [errors, touched])

  const getFieldStatus = useCallback(
    (field) => {
      const error = getError(field)
      return error ? 'error' : 'default'
    },
    [getError]
  )

  const getFieldError = useCallback(
    (field) => {
      return getError(field)
    },
    [getError]
  )

  const handleBlur = useCallback((field, value) => {
    const fieldError = validateField(field, value)
    setTouched((current) => ({ ...current, [field]: true }))
    setErrors((current) => ({
      ...current,
      [field]: fieldError,
    }))
  }, [])

  const handleApellidoChange = useCallback(
    (e) => {
      const value = e?.target?.value ?? e
      setApellido(value)
      if (touched.apellido) {
        setErrors((current) => ({
          ...current,
          apellido: validateField('apellido', value),
        }))
      }
    },
    [touched.apellido]
  )

  const handleNombreChange = useCallback(
    (e) => {
      const value = e?.target?.value ?? e
      setNombre(value)
      if (touched.nombre) {
        setErrors((current) => ({
          ...current,
          nombre: validateField('nombre', value),
        }))
      }
    },
    [touched.nombre]
  )

  const handlePhoneDataChange = useCallback(({ isValid }) => {
    setIsPhoneValid(isValid)
  }, [])

  const handleAltPhoneDataChange = useCallback(({ isValid }) => {
    setIsAltPhoneValid(isValid)
  }, [])

  const isFormValid = useMemo(() => {
    const isApellidoValid = apellido.trim().length >= 2
    const isNombreValid = nombre.trim().length >= 2
    const isTipoTelefonoValid = tipoTelefonoPrincipal.trim() !== ''
    const isNumeroTelefonoValid = numeroTelefonoPrincipal.trim() !== '' && isPhoneValid

    // El teléfono alternativo se valida como un único conjunto:
    // - ambos campos vacíos => válido (no obligatorio)
    // - ambos completos y número válido => válido
    // - cualquier combinación parcial o número inválido => inválido
    const tipoAlt = tipoTelefonoAlternativo.trim()
    const numAlt = numeroTelefonoAlternativo.trim()
    const isTelefonoAlternativoValid =
      (tipoAlt === '' && numAlt === '') || (tipoAlt !== '' && numAlt !== '' && isAltPhoneValid)

    return (
      isApellidoValid && isNombreValid && isTipoTelefonoValid && isNumeroTelefonoValid && isTelefonoAlternativoValid
    )
  }, [
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    isPhoneValid,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
    isAltPhoneValid,
  ])

  const hasChanges = useMemo(() => {
    const initial = initialSnapshot
    return (
      apellido !== (initial.apellido || '') ||
      nombre !== (initial.nombre || '') ||
      tipoTelefonoPrincipal !== (initial.tipoTelefonoPrincipal || '') ||
      numeroTelefonoPrincipal !== (initial.numeroTelefonoPrincipal || '') ||
      tipoTelefonoAlternativo !== (initial.tipoTelefonoAlternativo || '') ||
      numeroTelefonoAlternativo !== (initial.numeroTelefonoAlternativo || '')
    )
  }, [
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
    initialSnapshot,
  ])

  /**
   * Sincroniza en caliente el "estado inicial" del formulario con los datos
   * recién guardados, sin necesidad de recargar la página. hasChanges
   * vuelve a false y el botón Guardar cambios se deshabilita.
   * @param {Object} newData - Datos guardados que pasan a ser el nuevo initialData
   */
  const syncInitialData = useCallback((newData) => {
    if (!newData) return
    const merged = { ...initialDataRef.current, ...newData }
    initialDataRef.current = merged
    setInitialSnapshot(merged)
  }, [])

  const validateAllFields = useCallback(() => {
    const apellidoError = validateField('apellido', apellido)
    const nombreError = validateField('nombre', nombre)

    setTouched({
      apellido: true,
      nombre: true,
    })

    setErrors({
      apellido: apellidoError,
      nombre: nombreError,
    })

    return !(apellidoError || nombreError)
  }, [apellido, nombre])

  const resetForm = useCallback(() => {
    const initial = initialDataRef.current
    setApellido(initial.apellido || '')
    setNombre(initial.nombre || '')
    setTipoTelefonoPrincipal(initial.tipoTelefonoPrincipal || '')
    setNumeroTelefonoPrincipal(initial.numeroTelefonoPrincipal || '')
    setTipoTelefonoAlternativo(initial.tipoTelefonoAlternativo || '')
    setNumeroTelefonoAlternativo(initial.numeroTelefonoAlternativo || '')
    setTouched({})
    setErrors({})
    setIsPhoneValid(true)
    setIsAltPhoneValid(true)
  }, [])

  return {
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
    isFormValid,
    hasChanges,

    setTipoTelefonoPrincipal,
    setNumeroTelefonoPrincipal,
    setTipoTelefonoAlternativo,
    setNumeroTelefonoAlternativo,

    handleApellidoChange,
    handleNombreChange,
    handleBlur,
    handlePhoneDataChange,
    handleAltPhoneDataChange,

    getFieldStatus,
    getFieldError,
    validateAllFields,
    resetForm,
    syncInitialData,
  }
}
